import React from "react";
import {
  useFieldValue,
  useGetMissingFieldWarnings,
  useHasMLAPermission,
  useHasDataIndicatorsPermission,
} from "hooks";
import { useSelector } from "react-redux";
import {
  selectMLAFieldWarningsBySaleLotId,
  selectDataFieldWarningsBySaleLotId,
  selectNASFieldWarningsBySaleLotId,
  selectIsUnderValidationThersholdBySaleLotId,
  getDefaultRequiredMLAFields,
  getCurrentSale,
} from "selectors";
import { MissingFieldsIndicatorTypes } from "constants/saleLots";
import { startCase } from "lodash";
import { CombinedFormikMissingFieldIndicatorProps } from "./types";
import {
  MissingFieldsIndicatorBase,
  defaultNutrienWarnings,
} from "./MissingFieldsIndicator";
import { checkUnderThreshold } from "./NasMissingFieldsIndicator";

const CombinedFormikMissingFieldIndicatorInner: React.FC<
  CombinedFormikMissingFieldIndicatorProps
> = ({
  modelIds,
  missingMLAFieldsWarningEnabled,
  missingNASFieldWarningEnabled,
  overrideMissingFieldName,
  name,
  hideLabel = false,
}) => {
  const id = useFieldValue("id");
  const value = useFieldValue(name);
  const isComplete = !!value;
  const hasMLAPermission = useHasMLAPermission();
  const hasDataIndicatorsPermission = useHasDataIndicatorsPermission();

  const calculatedModelIds = modelIds.length > 0 ? modelIds : [id];

  // Use different selectors based on MLA permission
  // MLA users get actual MLA data, Data users get blank MLA structure from sale lots
  const missingMLAFieldLookup = useSelector(
    hasMLAPermission
      ? selectMLAFieldWarningsBySaleLotId
      : selectDataFieldWarningsBySaleLotId,
  );
  const missingNASFieldsLookup = useSelector(selectNASFieldWarningsBySaleLotId);
  const defaultMLAWarnings = useSelector(getDefaultRequiredMLAFields);

  const missingMLAFieldWarnings = useGetMissingFieldWarnings({
    modelIds: calculatedModelIds,
    lookup: missingMLAFieldLookup,
    defaultWarnings: defaultMLAWarnings,
  });

  const missingNASFieldWarnings = useGetMissingFieldWarnings({
    modelIds: calculatedModelIds,
    lookup: missingNASFieldsLookup,
    defaultWarnings: defaultNutrienWarnings,
  });

  const validations = useSelector(selectIsUnderValidationThersholdBySaleLotId);
  const underThreshold = checkUnderThreshold(validations, calculatedModelIds);

  const nameStartCase = startCase(overrideMissingFieldName || name);

  // MLA/Data indicators are not shown if not validated
  const showMLAIndicator = defaultMLAWarnings.includes(nameStartCase);

  // Only show indicator if user has either MLA or Data Indicators permission
  const hasMLAOrDataPermission =
    hasMLAPermission || hasDataIndicatorsPermission;

  // Determine the label based on whether user has MLA permission
  const indicatorLabel = hasMLAPermission
    ? MissingFieldsIndicatorTypes.MLA
    : MissingFieldsIndicatorTypes.DATA;

  return (
    <>
      {/* Only show MLA/Data indicator if NAS is not being shown */}
      {showMLAIndicator &&
        missingMLAFieldsWarningEnabled &&
        !missingNASFieldWarningEnabled &&
        hasMLAOrDataPermission && (
          <MissingFieldsIndicatorBase
            warnings={missingMLAFieldWarnings}
            label={indicatorLabel}
            includeLabel={!hideLabel}
            overrideNoMissingField={isComplete}
          />
        )}
      {missingNASFieldWarningEnabled && (
        <MissingFieldsIndicatorBase
          warnings={missingNASFieldWarnings}
          label={MissingFieldsIndicatorTypes.NAS}
          includeLabel={!hideLabel}
          overrideNoMissingField={
            !missingNASFieldWarnings.includes(
              startCase(overrideMissingFieldName || name),
            )
          }
          areFieldsUnderWarningThreshold={underThreshold}
        />
      )}
    </>
  );
};

// Wrapper component that only renders the inner component if indicators are enabled
// This prevents unnecessary hook and selector calls when nothing will be shown
export const CombinedFormikMissingFieldIndicator: React.FC<
  CombinedFormikMissingFieldIndicatorProps
> = props => {
  const { missingMLAFieldsWarningEnabled, missingNASFieldWarningEnabled } =
    props;

  const currentSale = useSelector(getCurrentSale);

  // Early return if neither indicator is enabled
  if (!missingMLAFieldsWarningEnabled && !missingNASFieldWarningEnabled) {
    return null;
  }

  // Early return if not in a sale
  if (!currentSale) {
    return null;
  }

  return <CombinedFormikMissingFieldIndicatorInner {...props} />;
};
